/**
 * The Hiring pages' pure half: the standards table and the words for each applicant and call.
 * Pure so it can be tested without a browser (Pages.render.test.mjs).
 *
 * ⛔ THE TABLE SAYS WHERE EACH ANSWER CAME FROM. CDL, experience, endorsements and where they live
 * come from the application (backend recruitingLib.mjs matchApplicant scores them). Home time is
 * asked on the call and read by the recruiter. Driving record and drug tests are NEVER asked on the
 * call (the call's absolute limits, AlfredVoice-v2 core freightRecruitContext.js: a third party
 * compiling them for an employer is a consumer reporting agency), so those rows always read
 * "Not asked on the call" and "Recruiter checks".
 */
import { experienceLabel } from "../recruitingView.js";

export const RESULT = {
  meets: { label: "Meets", bg: "#EAF0FD", fg: "#1F3F99" },
  misses: { label: "Doesn't meet", bg: "#FDEBDD", fg: "#8A3A0C" },
  check: { label: "Recruiter checks", bg: "#F0F0EE", fg: "#4A4A4A" },
  unstated: { label: "Not stated", bg: "#F0F0EE", fg: "#4A4A4A" },
};

const CDL = { A: "Class A", B: "Class B", C: "Class C", CLP: "A permit, no CDL yet", none: "No CDL" };
const ENDORSE = { H: "Hazmat", N: "Tanker", T: "Doubles and triples", X: "Tanker and hazmat", P: "Passenger", S: "School bus" };
const result = (ok) => (ok === true ? "meets" : ok === false ? "misses" : "unstated");
const listWords = (xs, last = "or") => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} ${last} ${xs[xs.length - 1]}`);

/**
 * The rows of the standards table, from the applicant and the carrier's standards (the shapes
 * backend recruitingLib.mjs applicantOut and normaliseCriteria send). `scored` rows are the ones the
 * application answers; the rest are for the recruiter.
 */
export function standardsRows(a, criteria = {}, { called = true } = {}) {
  const c = criteria || {};
  const rows = [];
  if (c.cdlClass) {
    const ok = !a.cdlClass ? null : ({ A: 3, B: 2, C: 1 }[a.cdlClass] ?? 0) >= ({ A: 3, B: 2, C: 1 }[c.cdlClass] ?? 0);
    rows.push({ key: "cdl", label: "CDL", require: CDL[c.cdlClass], said: CDL[a.cdlClass] || "Not on the application", result: result(ok), scored: true });
  }
  if (c.minMonths > 0) {
    const m = a.experienceMonths;
    const ok = m == null ? null : m >= c.minMonths;
    rows.push({ key: "experience", label: "Experience", require: `${experienceLabel(c.minMonths)} or more`, said: m == null ? "Not on the application" : experienceLabel(m), result: result(ok), scored: true });
  }
  for (const e of c.endorsements || []) {
    const have = new Set(a.endorsements || []);
    if (have.has("X")) { have.add("H"); have.add("N"); }
    const ok = have.has(e) ? true : (a.endorsements || []).length ? false : null;
    rows.push({ key: `endorse-${e}`, label: `${ENDORSE[e] || e} endorsement`, require: "Required", said: ok ? "Has it" : ok === false ? "Not listed" : "Not on the application", result: result(ok), scored: true });
  }
  if ((c.states || []).length) {
    const ok = !a.state ? null : c.states.includes(a.state);
    rows.push({ key: "area", label: "Hiring area", require: `Lives in ${listWords(c.states)}`, said: a.state ? `Lives in ${[a.city, a.state].filter(Boolean).join(", ")}` : "Not on the application", result: result(ok), scored: true });
  }
  rows.push({ key: "home", label: "Home time", require: c.homeTime || "Not on file", said: called ? "Asked on the call" : "Not called yet", result: "check", scored: false, readCall: called });
  rows.push({ key: "record", label: "Driving record", require: c.record || "Not on file", said: "Not asked on the call", result: "check", scored: false });
  rows.push({ key: "drugs", label: "Drug and alcohol tests", require: c.drugTests || "Not on file", said: "Not asked on the call", result: "check", scored: false });
  return rows;
}

/** The line over the table: how many of the application's standards are met, and which are not. */
export function standardsLine(rows) {
  const scored = rows.filter((r) => r.scored);
  if (!scored.length) return "No standards on file to compare the application with.";
  const met = scored.filter((r) => r.result === "meets").length;
  const missed = scored.filter((r) => r.result === "misses");
  const unstated = scored.filter((r) => r.result === "unstated");
  const parts = [`Meets ${met} of the ${scored.length} ${scored.length === 1 ? "standard" : "standards"} on the application.`];
  if (missed.length) parts.push(`${missed.length === 1 ? "Misses one" : `Misses ${missed.length}`}: ${missed.map((r) => r.label.toLowerCase()).join(", ")}.`);
  if (unstated.length) parts.push(`Not stated: ${unstated.map((r) => r.label.toLowerCase()).join(", ")}.`);
  return parts.join(" ");
}

export const SOURCE = { apply: "your application link", import: "your past-applicant list", manual: "added by hand" };

/** Moving an applicant on: what each status offers. */
export function actionsFor(status) {
  if (["new", "screening", "screened", "handed_off"].includes(status)) return ["forward", "not_now"];
  if (status === "forward") return ["hire", "not_now"];
  if (status === "not_now") return ["forward"];
  return [];
}
export const ACTION = { forward: "Move forward", not_now: "Not a fit", hire: "Hire" };
export const STATUS_LINE = {
  forward: "Moving forward.", not_now: "Not a fit.", hired: "Hired.",
  do_not_contact: "Asked not to be contacted. Alfred never calls them.",
};

const minutes = (call) => {
  const s = call?.startedAt ? new Date(call.startedAt).getTime() : NaN;
  const e = call?.endedAt ? new Date(call.endedAt).getTime() : NaN;
  if (!Number.isFinite(s) || !Number.isFinite(e) || e < s) return null;
  return Math.max(1, Math.round((e - s) / 60000));
};

/** One call, in the recruiter's words, with the time. A failed dial says it failed, never "called". */
export function callWords(call, when) {
  if (!call) return "Alfred has not called yet.";
  const at = when(call.startedAt || call.createdAt);
  if (call.status === "queued" || call.status === "placed") return "Alfred is on the call now.";
  if (call.status === "failed") return `The call did not go through${call.error ? ` (${String(call.error).replace(/_/g, " ")})` : ""}${at ? `, ${at}` : ""}.`;
  if (call.status === "no_answer") return `${call.outcome === "voicemail" ? "Voicemail, no message left" : "No answer"}${at ? `, ${at}` : ""}.`;
  const m = minutes(call);
  return `Talked with Alfred${at ? `, ${at}` : ""}${m ? `, ${m} min` : ""}.`;
}
